define([
    "dojo/_base/declare",
    "dojo/_base/lang",
    "dojo/keys",
    "mw-form/TextFieldMenuItem",
    "MW/toolstrip/constants/TypeConstants",
    "MW/toolstrip/mixins/property/EditableMixin",
    "MW/toolstrip/mixins/property/EditOptionMixin",
    "MW/toolstrip/mixins/property/RemovableMixin",
    "../mixins/property/TextOverrideMixin",
    "../mixins/property/TypeMixin",
    "mw-form/mixins/mixinDependencyValidator",
    "MW/uiframework/FocusManager"
], function (declare, lang, keys, TextFieldMenuItem, TypeConstants, EditableMixin, EditOptionMixin, RemovableMixin,
             TextOverrideMixin, TypeMixin, mixinDependencyValidator, FocusManager) {

    var TEXT_FIELD_VALUE_CONTAINER_CLASS = "mwTextFieldValueContainer";
    var TEXT_FIELD_VALUE_LINE_CLASS = "mwTextFieldValueLine";

    return declare(mixinDependencyValidator.validate([TextFieldMenuItem, TextOverrideMixin,
        EditableMixin, EditOptionMixin, RemovableMixin, TypeMixin]), {
        visualFamily: "default",
        eligibleForParentAdditionToQAB: true, // Property used to know that we need to add the parent of this widget to QAB

        //Cant map label to text and text to value for this widget
        //GetWidgetProperties in widgetManager would not work as expected as it replaces the actual value of text if a label is passed.

        constructor: function () {
            //Override typing event mixin of textfield widget to emit "text" instead of "currentContent"
            this.textField._triggerTypingEvent = function (value) {
                this.emit("typing", {
                    mwEventData: {
                        text: value
                    }
                });
            };

            this._defaultTextFieldWidth = this.textField.get("width");
        },
        _setLabelAttr: function (value) {
            this.set("text", value);
            this._set("label", value);
        },

        _getLabelAttr: function () {
            return this.get("text");
        },

        _setValueAttr: function (value) {
            this.inherited(arguments);

            if (!this.get("editable") && this._textFieldValueContainer) {
                this._textFieldValueLine.textContent = this.textField.get("value");
            }
        },

        /**
         * Override the editable property to toggle the state of the widget between:
         * 1. editable: Text field displayed to accept user input
         * 2. un-editable: Contents of the text field displayed as simple text. No text field shown.
         * @param value
         * @private
         */
        _setEditableAttr: function (value) {
            if (value) {
                this._resizeTextField();
                this._valueBeforeEdit = this.get("value");
                this._addDocumentClickListener();
                // Popup should always be prevented from closing when clicked on list item in
                // editable state
                this.set("closeMenuOnClick", false);
            } else {
                if(!this._textFieldValueContainer) {
                    // Create the non-editable node for text field value lazily if not created
                    // already
                    this._createNonEditableValueNode();
                }
                this._textFieldValueLine.textContent = this.textField.get("value");
                this._removeDocumentClickListener();
            }
            this.inherited(arguments);
        },

        /**
         * Sets the width of the text field widget.
         * @param value
         * @private
         */
        _setTextFieldWidthAttr: function(value) {
            this.textField.set("width", value);
        },

        buildRendering: function () {
            this.inherited(arguments);
        },

        postCreate: function () {
            // Add keydown event listener to the widget before the TextFieldMenuItem widget adds
            // it so that we can get the key event first and handle it accordingly
            // for ENTER key.
            this.domNode.addEventListener("keydown", this._handleKeyDown.bind(this), true);
            this.inherited(arguments);
            this.textField.set("visualFamily", "default");
            this.watch("text", lang.hitch(this, "_updateLabel"));
            this.domNode.addEventListener("edit", this._handleEdit.bind(this));
            // Cache the user defined behaviour for popup close on click
            this._closeMenuOnClick = this.get("closeMenuOnClick");
        },

        /**
         * Creates the dom node that displays the contents of text field in non-editable state
         * of widget.
         * @private
         */
        _createNonEditableValueNode: function() {
            // Create dom nodes for displaying the text field contents in non-editable state
            this._textFieldValueContainer = document.createElement("div");
            this._textFieldValueContainer.classList.add(TEXT_FIELD_VALUE_CONTAINER_CLASS);
            this._textFieldValueLine = document.createElement("span");
            this._textFieldValueContainer.appendChild(this._textFieldValueLine);
            this._textFieldValueLine.classList.add(TEXT_FIELD_VALUE_LINE_CLASS);
            this.domNode.insertBefore(this._textFieldValueContainer, this.shortcutKeyNode);
        },

        /**
         * Overriding this method from the base widget (TextFieldMenuItem).
         * If the widget is in editable state, the focusNode should be the text field.
         * If its not in editable state, the focusNode should be the widget dom node.
         * @returns {Node}
         */
        getFocusNode: function () {
            if (this.get("editable") === false) {
                return this.focusNode;
            }
            return this.textField.focusNode;
        },

        /**
         * If the popup list is closed by clicking outside it or a close is trigger when value is 
         * commited by hitting enter
         * @private
         */
        _handleMenuClose: function () {
            if (this.get("editOption") && this.get("editable")) {
                if (!this._commit) {
                    // revert the value if it wasn't a commit
                    this.set("value", this._valueBeforeEdit);    
                }

                // reset the flag
                this._commit = false;

                this._disallowEditingAndResetPopupClose();
                this._removeMenuCloseListener();
            }
        },

        /**
         * When 'edit' event is emitted, do the following.
         * If the widget doesn't have hasDynamicEdit, widget should enter editable state and
         * stop 'edit' event propagation. Else, 'edit' event should continue to emit so UI author
         * can respond to it.
         * @param evt edit event
         * @private
         */
        _handleEdit: function (evt) {
            if (!this.get("hasDynamicEdit")) {
                evt.stopPropagation();
                evt.stopImmediatePropagation();
                this.set("editable", true);
            }
        },

        /**
         * API to select text inside the text field. It accepts a selection range object that
         * has start and end properties indicating the range of text to be selected.
         *
         * If start position is not specified or if it is less than 0, it will be reset to 0.
         * If end position is not specified or if it is greater than the length of text in text
         * field, it will be reset to the length of text.
         * @param selectionRange range of text to be selected
         * {
         *     start:
         *     end:
         * }
         * @private
         */
        _setSelectionAttr: function (selectionRange) {
            var inputNode = this.textField.inputNode;
            var start = selectionRange.start;
            var end = selectionRange.end;
            if (!start || start < 0) {
                start = 0;
            }

            if (!end || end > inputNode.value.length) {
                end = inputNode.value.length;
            }
            inputNode.focus();
            inputNode.setSelectionRange(start, end);
        },

        /**
         * Handles ENTER key when focus is in the text field widget. If the widget is in
         * editable state and it doesn't have a dynamic edit option, the default behaviour
         * should be to commit the list item value and convert the list item to non-editable
         * state. Also prevent the enter key event from propagating to the parent TextFieldMenuItem.
         * If the widget has dynamic edit option, then continue emitting "commit" or "recommit"
         * events through the TextFieldMenuItem class.
         * @param evt
         * @private
         */
        _handleKeyDown: function (evt) {
            var keyCode = evt.keyCode;
            if (keyCode === keys.ENTER) {

                // If widget is in editable state, then emit commit/recommit events for ENTER key
                if (this.get("editable") && !this.get("hasDynamicEdit") && this.get("editOption")) {
                    // Commit the widget and text field value and emit commit/recommit events

                    var oldValue = this.get("value"),
                        newValue = this.textField.inputNode.value;

                    // set the flag    
                    this._commit = true;

                    // Emit commit/recommit events
                    if (oldValue === newValue) {
                        // Emit a "recommit" event in case someone wants to listen for all [Enter] key presses.
                        this.emit("recommit", {mwEventData: {propertyName: "value", value: newValue}});
                    } else {
                        // Emit a "commit" event signaling that the [Enter] key was hit.  And, commit the value.
                        this.emit("commit", {mwEventData: {propertyName: "value", value: newValue}});
                        // For ENTER key, update the list item value to be equal to text field contents
                        this.set("value", newValue);

                        // Emit a change event for backwards compatability
                        this.textField._triggerChangeEvent("value", oldValue, newValue);
                    }

                    if (!FocusManager.isAvailable()) {
                        this._handleMenuCloseOnEnter();
                    }

                    // On ENTER key (when edit option is available and dynamicEdit isn't
                    // available), set editable state to false and revert closeMenuOnClick to
                    // user set value
                    this._disallowEditingAndResetPopupClose();

                    // Prevent the key from propagating to TextFieldMenuItem and return. If this
                    // step is omitted, TextFieldMenuItem will handle the ENTER key again and emit
                    // recommit event right after the commit event.
                    evt.stopImmediatePropagation();

                    return;

                } else if(!this.get("editable")) {
                    // Emit itemPushed event if widget is non-editable state and ENTER key is
                    // pressed
                    this.emit("itemPushed");
                    this._handleMenuCloseOnEnter();
                }


            }
            this.inherited(arguments);
        },

        _handleMenuCloseOnEnter: function () {
            if (this.closeOnEnter && (this.closeOnEnter === true)) {
                this._requestClose(false);
            }
        },

        /**
         * If the click target is not within the widget dom node, then exit editable state of
         * the widget without committing the changes.
         * @param evt click event
         * @private
         */
        _handleDocumentBodyClick: function (evt) {
            var target = evt.target;
            if (!this.domNode.contains(target)) {
                this.set("value", this._valueBeforeEdit);
                this._disallowEditingAndResetPopupClose();

            }
        },

        _updateLabel: function (property, oldValue, newValue) {
            this.set("label", newValue);
        },

        _onFocus: function () {
            this.inherited(arguments);
            this.emit("focusgained", {mwEventData: {text: this.textField.inputNode.value}});
            this._addMenuCloseListener();
        },

        _onBlur: function () {
            this.inherited(arguments);
            this.emit("focuslost", {mwEventData: {text: this.textField.inputNode.value}});
        },

        /**
         * Sets the editable state of the widget to false so user cannot edit the text field
         * contents. When the widget goes from editable state to non-editable state, we need to
         * reset the closeMenuOnPopup property to the user set value. This is necessary because
         * we prevent the popup from closing when widget is in editable state. Therefore, the popup
         * behaviour needs to be reverted back to the user defined one.
         * @private
         */
        _disallowEditingAndResetPopupClose: function() {
            this.set("editable", false);
            this.set("closeMenuOnClick", this._closeMenuOnClick);
        },

        /**
         * Resize the text field to be of the same width as the text displayed in non-editable
         * state. This is to ensure that the popup list width doesn't change when toggling the
         * editable state of widget and there is a smooth transition between the toggled states.
         * The minimum width of the text field widget will be set to its default width(defined
         * by mw-form/textField).
         * @private
         */
        _resizeTextField: function () {
            this._textFieldValueContainerWidth = 0;
            if(this._textFieldValueContainer) {
                this._textFieldValueContainerWidth = this._textFieldValueContainer.getBoundingClientRect().width;
            }

            if (this._textFieldValueContainerWidth !== 0) {
                this._textFieldWidth = this._textFieldValueContainerWidth < this._defaultTextFieldWidth ? this._defaultTextFieldWidth : this._textFieldValueContainerWidth;
                this.set("textFieldWidth", this._textFieldWidth);
            }
        },

        /**
         * Adds a click event listener to the document body if the widget has the edit context
         * menu option and does not have a dynamic edit option. In such state of the widget, if
         * users click on the edit context menu option, the default behaviour of widget is to
         * make the widget editable. Similarly, when a click happens outside the widget in such
         * cases, the default behaviour should be to exit editable state.
         * @private
         */
        _addDocumentClickListener: function () {
            // If hasDynamicEdit is false, then editable state of widget will be toggled by
            // default. In such a case, attach listener so that editable state can be exited if
            // a click happens somewhere else other than on the widget
            // This event listener should be removed when the widget exits editable state
            if (this.get("editOption") && !this.get("hasDynamicEdit")) {
                if (!this._documentClickCallback) {
                    // Create and add the event listener only if it hasn't been created already
                    this._documentClickCallback = this._handleDocumentBodyClick.bind(this);
                    document.body.addEventListener("click", this._documentClickCallback);
                }
            }
        },

        /**
         * Removes the click listener from the document body if it exists.
         * @private
         */
        _removeDocumentClickListener: function () {
            if (this._documentClickCallback) {
                document.body.removeEventListener("click", this._documentClickCallback);
                this._documentClickCallback = null;
            }
        },

        /**
         * Adds event listener for menu close events if there isn't an existing one already.
         * @private
         */
        _addMenuCloseListener: function() {
            // g2081758: The "close" event gets fired on the PopupList from mw-form/mixins/OnCloseMixin. Hence, we need to
            // skip any other ancestors like PopupListPanel and get the PopupList to set the "close" event listener on it.
            this._parentMenu = this._getAncestorPopupList();
            if (this._parentMenu && !this._menuCloseCallback) {
                this._menuCloseCallback = this._handleMenuClose.bind(this);
                // Event listener for menu close event is added(if not already added) on focus
                // to determine correctly if widget parent exists or not.
                this._parentMenu.domNode.addEventListener("close", this._menuCloseCallback);
            }
        },

        /**
         * Gets the ancestor popup list by skipping any other ancestors like PopupListPanel in between.
         * @private
         */
        _getAncestorPopupList: function () {
            var parent = this.getParent ? this.getParent() : null;
            while (parent && parent.get("type") !== TypeConstants.POPUP_LIST) {
                parent = parent.getParent ? parent.getParent() : null;
            }

            return parent;
        },

        /**
         * Removes the menu close event listener if one was added previously.
         * The event listener should be removed when menu closes and when the widget is
         * destroyed as the event listener is added when the widget gets focus.
         * @private
         */
        _removeMenuCloseListener: function() {
            if(this._menuCloseCallback && this._parentMenu && this._parentMenu.domNode) {
                this._parentMenu.domNode.removeEventListener("close", this._menuCloseCallback);
            }
            this._menuCloseCallback = null;
        },

        startup: function () {
            this.inherited(arguments);
        },

        destroy: function () {
            this._removeDocumentClickListener();
            this._removeMenuCloseListener();
            this.inherited(arguments);
        }
    });
});
